import PropTypes from "prop-types";
import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
// @mui
import { styled } from "@mui/material/styles";
import { Box, Link, Drawer, Typography, Avatar, Tooltip } from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
// hooks
import useResponsive from "../../../hooks/useResponsive";
// components
import Logo from "../../../components/logo";
import Scrollbar from "../../../components/scrollbar";
import NavSection from "../../../components/nav-section";
//
import navConfig from "./config";
import { useSelector, useDispatch } from "react-redux";
import { fetchMyPhoto, setPhotoUrl } from "../../../feature/userSlice";
import ApiCall from "../../../utils/apicall";

// ─────────────────────────────────────────────────────────────────────────────

const NAV_WIDTH = 260;

// On the SFM dark chrome (#1D2330), use a subtle white-tinted account
// surface for legibility — mirrors iOS SFMBrandHeader user-card pattern.
const StyledAccount = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: "10px 12px",
  borderRadius: 10,
  backgroundColor: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.08)",
}));

// Branded sidebar footer — mirrors iOS SFMBrandFooter pattern.
const StyledBrandFooter = styled("div")(({ theme }) => ({
  padding: "12px 20px",
  borderTop: "1px solid rgba(255,255,255,0.10)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: 11,
  color: "rgba(255,255,255,0.55)",
  letterSpacing: 0.3,
  fontFeatureSettings: "'tnum' 1",
}));

// ----------------------------------------------------------------------

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

export default function Nav({ openNav, onCloseNav }) {
  const { pathname } = useLocation();
  // review-fix F-06: loginUser is '' on logout; default to {} so .firstName/.email don't crash the sidebar.
  const decodedToken = useSelector((state) => state.userSlice.loginUser)?.decodedToken ?? {};
  const photoUrl = useSelector((state) => state.userSlice.photoUrl);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const isDesktop = useResponsive("up", "lg");

  // Load photo once on mount
  useEffect(() => {
    dispatch(fetchMyPhoto());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Resize + convert to base64 via canvas
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = async () => {
      const size = 200;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      // Center-crop to square
      const aspect = img.width / img.height;
      let sx = 0, sy = 0, sw = img.width, sh = img.height;
      if (aspect > 1) { sx = (img.width - img.height) / 2; sw = img.height; }
      else { sy = (img.height - img.width) / 2; sh = img.width; }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, size, size);
      const photoData = canvas.toDataURL("image/jpeg", 0.85);
      URL.revokeObjectURL(objectUrl);
      try {
        await ApiCall.put("/user/me/photo", { photoData });
        dispatch(setPhotoUrl(photoData));
      } catch (err) {
        console.error("Photo upload failed", err);
      }
    };
    img.src = objectUrl;
    e.target.value = ""; // reset so same file can be re-selected
  }, [dispatch]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        "& .simplebar-content": {
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box sx={{ px: 2.5, py: 3, display: "inline-flex" }}>
        <Logo />
      </Box>

      <Box sx={{ mb: 5, mx: 2.5 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <Link underline="none">
          <StyledAccount>
            <Tooltip title="Click to change photo" placement="right">
              <Box
                onClick={handleAvatarClick}
                sx={{ position: "relative", cursor: "pointer", flexShrink: 0,
                  "&:hover .cam-overlay": { opacity: 1 } }}
              >
                <Avatar
                  src={photoUrl || undefined}
                  alt={decodedToken.firstName}
                  sx={{ width: 40, height: 40 }}
                />
                <Box
                  className="cam-overlay"
                  sx={{
                    position: "absolute", inset: 0, borderRadius: "50%",
                    bgcolor: "rgba(0,0,0,0.45)", display: "flex",
                    alignItems: "center", justifyContent: "center",
                    opacity: 0, transition: "opacity 0.2s",
                  }}
                >
                  <CameraAltIcon sx={{ fontSize: 16, color: "#fff" }} />
                </Box>
              </Box>
            </Tooltip>

            <Box sx={{ ml: 2, overflow: "hidden" }}>
              {/* SFM brand chrome — flip user-card text to white tokens */}
              <Typography variant="subtitle2" sx={{ color: "brand.onChrome" }} noWrap>
                {decodedToken.firstName + " " + decodedToken.lastName}
              </Typography>

              <Typography variant="body2" sx={{ color: "brand.onChromeSubtle" }} noWrap>
                {decodedToken.email}
              </Typography>
            </Box>
          </StyledAccount>
        </Link>
      </Box>

      <NavSection data={navConfig} />

      <Box sx={{ flexGrow: 1 }} />

      {/* Branded sidebar footer — mirrors iOS SFMBrandFooter §F-003 Row 14 */}
      <StyledBrandFooter>
        <Box component="span" sx={{ fontWeight: 600, color: "brand.accent", letterSpacing: 0.5 }}>
          SHOP FLOOR MGMT
        </Box>
        <Box component="span">v{process.env.REACT_APP_VERSION || "1.0"}</Box>
      </StyledBrandFooter>
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV_WIDTH },
      }}
    >
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              // SFM dark-slate chrome
              backgroundColor: "#1D2330",
              backgroundImage: "none",
              borderRight: "1px solid rgba(255,255,255,0.06)",
              boxShadow: "2px 0 20px rgba(0,0,0,0.20)",
              color: "rgba(255,255,255,0.92)",
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              // SFM dark-slate chrome (mobile drawer)
              backgroundColor: "#1D2330",
              backgroundImage: "none",
              borderRight: "1px solid rgba(255,255,255,0.06)",
              boxShadow: "2px 0 20px rgba(0,0,0,0.20)",
              color: "rgba(255,255,255,0.92)",
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
